/**
 * Firebase-specific Type Definitions
 * 
 * Types for Firebase services, user data, and round tracking
 * as specified in the project requirements.
 */

import { Timestamp } from 'firebase/firestore';
import { SessionStats, FingerType } from './index';

// User Schema Types
export interface FirebaseUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Profile information
  profile: {
    firstName?: string;
    lastName?: string;
    timezone?: string;
    language?: string;
  };

  // Training preferences
  preferences: {
    enableSounds: boolean;
    enableHapticFeedback: boolean;
    showFingerHints: boolean;
    preferredDifficulty: 'beginner' | 'intermediate' | 'advanced';
  };

  // Overall statistics
  overallStats: {
    totalSessions: number;
    totalTrainingTime: number; // in milliseconds
    bestAccuracy: number;
    bestSpeed: number;
    bestWPM: number;
    currentStreak: number;
    longestStreak: number;
  };

  // Last session info
  lastSession: {
    date: Timestamp;
    trainingType: 'defTraining' | 'customTraining';
    dayNumber?: number;
    roundNumber?: number;
  } | null;
}

// User Round Schema (EXACT specification)
export interface FirebaseUserRound {
  // Round identification
  roundId: string;
  uid: string;

  // Training context
  trainingType: 'defTraining' | 'customTraining';
  defTrainingDay?: number; // training day if defTraining (optional for custom)
  defTrainingRound?: number; // # of round in this Training day if defTraining (optional for custom)

  // Session metadata
  timestamp: Timestamp;
  duration: number; // in milliseconds

  // Training configuration (SPECIFICATION FORMAT)
  fingers: string[]; // ['big', 'pointing', 'middle', 'ring', 'pinky'] format
  hands: 'left' | 'right' | 'both';
  complexity: '1' | '1_3' | '1_5' | 'w'; // complexity setting

  // Per-letter statistics (EXACT field names)
  letters: {
    [letter: string]: {
      tapTotal: number; // total number of taps on this letter
      tapCorrect: number;
      tapWrong: number;
      tapFastest: number; // fastest tap on this letter in milliseconds
    };
  };

  // Word-based statistics (EXACT field names)
  words: {
    wordsTotal: number;
    wordsCorrect: number;
    wordsWrong: number;
    word1Fastest: number; // fastest 1-letter word in milliseconds
    word2Fastest: number;
    word3Fastest: number;
    word4Fastest: number;
    word5Fastest: number;
    word6Fastest: number;
    word7Fastest: number; // fastest 7-letter word in milliseconds
  };

  // Overall round statistics
  overallStats: SessionStats;
}

// Firebase Collection Names
export const FIREBASE_COLLECTIONS = {
  USERS: 'users',
  USER_ROUNDS: 'userRounds',
  // Sub-collections of users/{uid}
  ONE_WORD_RECORDS: 'oneWordRecords',
  ONE_WORD_RACES: 'oneWordRaces'
} as const;

// Letter statistics helper type
export interface LetterStats {
  letter: string;
  tapTotal: number;
  tapCorrect: number;
  tapWrong: number;
  tapFastest: number;
  accuracy: number; // calculated field
  averageSpeed: number; // calculated field
}

// Word statistics helper type
export interface WordStats {
  length: number;
  total: number;
  correct: number;
  wrong: number;
  fastest: number;
  accuracy: number; // calculated field
  averageSpeed: number; // calculated field
}

// Training session creation data
export interface CreateRoundData {
  trainingType: 'defTraining' | 'customTraining';
  defTrainingDay?: number;
  defTrainingRound?: number;
  fingers: FingerType[]; // Will be converted to specification format
  hands: 'left' | 'right' | 'both';
  complexity: '1' | '1_3' | '1_5' | 'w';
  duration: number;
  letters?: FirebaseUserRound['letters'];
  words?: FirebaseUserRound['words'];
  overallStats: SessionStats;
}

// User creation data
export interface CreateUserData {
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  profile?: Partial<FirebaseUser['profile']>;
  preferences?: Partial<FirebaseUser['preferences']>;
}

// Query filters for rounds
export interface RoundQueryFilters {
  trainingType?: 'defTraining' | 'customTraining';
  defTrainingDay?: number;
  dateFrom?: Date;
  dateTo?: Date;
  minAccuracy?: number;
  minSpeed?: number;
  limit?: number;
}

// Statistics aggregation types
export interface UserStatistics {
  totalRounds: number;
  totalTrainingTime: number;
  averageAccuracy: number;
  averageSpeed: number;
  averageWPM: number;
  bestRound: {
    roundId: string;
    accuracy: number;
    speed: number;
    wpm: number;
    date: Timestamp;
  } | null;
  recentImprovement: {
    accuracyTrend: number; // percentage change
    speedTrend: number; // percentage change
    period: number; // days
  };
  fingerPerformance: {
    [finger in FingerType]: {
      totalTaps: number;
      accuracy: number;
      averageSpeed: number;
    };
  };
  letterPerformance: {
    [letter: string]: LetterStats;
  };
  progressByDay: {
    [day: number]: {
      roundsCompleted: number;
      averageAccuracy: number;
      averageSpeed: number;
      bestRound: string | null;
    };
  };
}

// Firebase error types
export interface FirebaseError {
  code: string;
  message: string;
  details?: any;
}

// Real-time listener types
export type UserListener = (user: FirebaseUser | null, error?: FirebaseError) => void;
export type RoundsListener = (rounds: FirebaseUserRound[], error?: FirebaseError) => void;
export type StatsListener = (stats: UserStatistics | null, error?: FirebaseError) => void;

// Firebase service response types
export interface FirebaseServiceResponse<T = any> {
  success: boolean;
  data?: T;
  error?: FirebaseError;
}

// Authentication state
export interface AuthState {
  isAuthenticated: boolean;
  user: FirebaseUser | null;
  loading: boolean;
  error: FirebaseError | null;
}

// Default values for new users
export const DEFAULT_USER_PREFERENCES: FirebaseUser['preferences'] = {
  enableSounds: false,
  enableHapticFeedback: false,
  showFingerHints: true,
  preferredDifficulty: 'beginner'
};

export const DEFAULT_USER_STATS: FirebaseUser['overallStats'] = {
  totalSessions: 0,
  totalTrainingTime: 0,
  bestAccuracy: 0,
  bestSpeed: 0,
  bestWPM: 0,
  currentStreak: 0,
  longestStreak: 0
};

// Helper function types
export type LetterStatsCalculator = (rounds: FirebaseUserRound[]) => { [letter: string]: LetterStats };
export type WordStatsCalculator = (rounds: FirebaseUserRound[]) => { [length: number]: WordStats };
export type StatisticsAggregator = (rounds: FirebaseUserRound[]) => UserStatistics;
